/**
 * AI Insights Tab Component for RNCM Productivity - Futuristic Design
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Brain, Sparkles, Loader2, Zap, TrendingUp, AlertTriangle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

export const AIInsightsTab = ({ 
  aiAnalysis, 
  analyzingAI, 
  onRunAnalysis 
}) => {
  return (
    <div className="space-y-4">
      {/* Futuristic Header */}
      <div className="relative overflow-hidden rounded-2xl bg-slate-900/80 backdrop-blur-xl border border-fuchsia-500/30 p-6" style={{ boxShadow: '0 0 40px rgba(217, 70, 239, 0.2)' }}>
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/10 via-purple-500/5 to-cyan-500/10" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-fuchsia-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-3">
              <div className="p-2 bg-fuchsia-500/20 rounded-xl animate-pulse">
                <Brain className="h-6 w-6 text-fuchsia-400" style={{ filter: 'drop-shadow(0 0 10px rgba(217, 70, 239, 0.8))' }} />
              </div>
              AI-Powered Productivity Analysis
            </h3>
            <p className="text-slate-400 mt-2 text-sm">
              Predictive insights, risk alerts, and recommendations powered by advanced AI
            </p>
          </div>
          <Button 
            onClick={onRunAnalysis} 
            disabled={analyzingAI}
            className="bg-gradient-to-r from-fuchsia-500 via-purple-500 to-cyan-500 hover:from-fuchsia-400 hover:via-purple-400 hover:to-cyan-400 text-white border-0 shadow-lg shadow-fuchsia-500/30 px-6 py-2 font-semibold transition-all hover:scale-105"
          >
            {analyzingAI ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" style={{ filter: 'drop-shadow(0 0 5px white)' }} />
                Generate AI Analysis
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Content Area */}
      <div className="relative overflow-hidden rounded-2xl bg-slate-900/60 backdrop-blur-xl border border-slate-700/50" style={{ boxShadow: '0 0 20px rgba(217, 70, 239, 0.1)' }}>
        {!aiAnalysis ? (
          <div className="text-center py-16">
            <div className="relative inline-block">
              <Brain className="h-20 w-20 text-fuchsia-500/30 mx-auto mb-6" />
              <div className="absolute inset-0 animate-ping">
                <Brain className="h-20 w-20 text-fuchsia-500/10 mx-auto" />
              </div>
            </div>
            <p className="text-xl font-semibold text-white mb-2">No AI Analysis Yet</p>
            <p className="text-slate-400 max-w-md mx-auto">
              Click "Generate AI Analysis" to unlock predictive insights,<br />
              risk alerts, and intelligent recommendations for your team.
            </p>
            
            {/* Feature highlights */}
            <div className="grid grid-cols-3 gap-6 mt-8 max-w-2xl mx-auto px-6">
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Zap className="h-8 w-8 text-amber-400 mx-auto mb-2" style={{ filter: 'drop-shadow(0 0 8px rgba(251, 191, 36, 0.5))' }} />
                <p className="text-sm text-slate-300 font-medium">Instant Insights</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <TrendingUp className="h-8 w-8 text-emerald-400 mx-auto mb-2" style={{ filter: 'drop-shadow(0 0 8px rgba(52, 211, 153, 0.5))' }} />
                <p className="text-sm text-slate-300 font-medium">Trend Analysis</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <AlertTriangle className="h-8 w-8 text-rose-400 mx-auto mb-2" style={{ filter: 'drop-shadow(0 0 8px rgba(251, 113, 133, 0.5))' }} />
                <p className="text-sm text-slate-300 font-medium">Risk Alerts</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {/* Stats bar */}
            <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
              <span className="text-sm text-slate-400">
                Generated: <span className="text-fuchsia-400">{new Date(aiAnalysis.generated_at).toLocaleString()}</span>
              </span>
              <div className="flex gap-6">
                <div className="text-center">
                  <span className="text-2xl font-bold text-white">{aiAnalysis.data_summary?.total_clinicians || 0}</span>
                  <p className="text-xs text-slate-400">Clinicians</p>
                </div>
                <div className="text-center">
                  <span className="text-2xl font-bold text-rose-400">{aiAnalysis.data_summary?.over_capacity || 0}</span>
                  <p className="text-xs text-slate-400">Over Capacity</p>
                </div>
              </div>
            </div>
            
            <ScrollArea className="h-[500px] rounded-xl border border-slate-700/30 bg-slate-800/20 p-6">
              <div className="prose prose-sm max-w-none prose-invert">
                <ReactMarkdown
                  components={{
                    h1: ({children}) => <h1 className="text-xl font-bold mt-4 mb-2 text-fuchsia-400" style={{ textShadow: '0 0 20px rgba(217, 70, 239, 0.5)' }}>{children}</h1>,
                    h2: ({children}) => <h2 className="text-lg font-bold mt-4 mb-2 text-cyan-400" style={{ textShadow: '0 0 15px rgba(34, 211, 238, 0.5)' }}>{children}</h2>,
                    h3: ({children}) => <h3 className="text-base font-semibold mt-3 mb-1 text-purple-400">{children}</h3>,
                    p: ({children}) => <p className="my-2 leading-relaxed text-slate-300">{children}</p>,
                    ul: ({children}) => <ul className="list-disc ml-4 my-2 space-y-1 text-slate-300">{children}</ul>,
                    ol: ({children}) => <ol className="list-decimal ml-4 my-2 space-y-1 text-slate-300">{children}</ol>,
                    li: ({children}) => <li className="leading-relaxed">{children}</li>,
                    strong: ({children}) => <strong className="font-semibold text-white">{children}</strong>,
                  }}
                >
                  {aiAnalysis.analysis}
                </ReactMarkdown>
              </div>
            </ScrollArea>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIInsightsTab;
